import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Star,
  ShieldCheck,
  Truck,
  RotateCcw,
  ChevronLeft,
  ShoppingBag,
  Minus,
  Plus,
  Check,
} from "lucide-react";
import ProductCard from "../components/ProductCard";
import { useProduct } from "../hooks/useShop";
import { useCart } from "../context/CartContext";
import { SHOPIFY } from "../mock/mock";

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { product, products, loading } = useProduct(slug);
  const { addItem } = useCart();
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState("beschreibung");
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-[3/4] bg-[#ece4d6] animate-pulse rounded-sm" />
        <div className="space-y-4">
          <div className="h-6 w-40 bg-[#ece4d6] animate-pulse" />
          <div className="h-12 w-3/4 bg-[#ece4d6] animate-pulse" />
          <div className="h-24 w-full bg-[#ece4d6] animate-pulse" />
          <div className="h-14 w-full bg-[#ece4d6] animate-pulse" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 py-40 text-center">
        <h1 className="font-serif text-3xl text-[#17140f] mb-4">
          Produkt nicht gefunden
        </h1>
        <Link to="/shop" className="text-gold hover-underline">
          Zurück zum Shop
        </Link>
      </div>
    );
  }

  const related = products.filter((p) => p.id !== product.id).slice(0, 4);

  const handleAddToCart = () => {
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const buyNow = () => {
    // Direct single-product Shopify checkout with selected quantity
    window.location.href = `${SHOPIFY.domain}/cart/${product.variant_id}:${qty}`;
  };

  return (
    <div className="pt-6">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#6b665a] text-xs tracking-[0.15em] uppercase hover:text-gold transition-colors"
        >
          <ChevronLeft size={15} /> Zurück
        </button>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 pb-20">
        {/* Gallery */}
        <div className="flex flex-col-reverse lg:flex-row gap-4">
          <div className="flex lg:flex-col gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`w-16 h-20 overflow-hidden border transition-colors ${
                  activeImg === i ? "border-gold" : "border-[#e7e0d3]"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 aspect-[3/4] bg-[#ece4d6] overflow-hidden">
            <img
              src={product.images[activeImg]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Info */}
        <div className="lg:py-4">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-3">
            {product.family} · {product.category}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#17140f] leading-tight">
            {product.name}
          </h1>
          <p className="text-[#6b665a] mt-2">{product.subtitle}</p>

          <div className="flex items-center gap-2 mt-4">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={15}
                  className={
                    i < Math.round(product.rating)
                      ? "fill-gold text-gold"
                      : "text-[#d6cebf]"
                  }
                />
              ))}
            </div>
            <span className="text-[#6b665a] text-sm">
              {product.rating} · {product.reviews} Bewertungen
            </span>
          </div>

          <div className="flex items-baseline gap-3 mt-6">
            {product.oldPrice && (
              <span className="text-[#8d8779] text-lg line-through">
                €{product.oldPrice.toFixed(2)}
              </span>
            )}
            <span className="text-[#17140f] text-3xl font-serif">
              €{product.price.toFixed(2)}
            </span>
            <span className="text-[#8d8779] text-sm">/ {product.size}</span>
          </div>

          <p className="text-[#4a463c] leading-relaxed mt-6 max-w-lg">
            {product.description}
          </p>

          <p className="text-gold text-[11px] tracking-[0.2em] uppercase mt-5">
            {product.concentration}
          </p>

          {/* Live availability */}
          <div className="flex items-center gap-2 mt-4">
            <span
              className={`w-2 h-2 rounded-full ${
                product.available ? "bg-emerald-400" : "bg-[#8d8779]"
              }`}
            />
            <span className="text-xs text-[#6b665a]">
              {product.available ? "Auf Lager · sofort lieferbar" : "Ausverkauft"}
            </span>
          </div>

          {/* Quantity + Add to cart */}
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-[#dcd4c5]">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-4 py-4 text-[#4a463c] hover:text-gold transition-colors"
                aria-label="Weniger"
              >
                <Minus size={15} />
              </button>
              <span className="px-4 text-[#17140f] w-10 text-center">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                className="px-4 py-4 text-[#4a463c] hover:text-gold transition-colors"
                aria-label="Mehr"
              >
                <Plus size={15} />
              </button>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={!product.available}
              className="flex-1 bg-gold text-[#14110c] text-[12px] tracking-[0.25em] uppercase py-4 font-medium hover:bg-[#d9b45c] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {added ? (
                <>
                  <Check size={16} /> Hinzugefügt
                </>
              ) : (
                <>
                  <ShoppingBag size={16} />{" "}
                  {product.available ? "In den Warenkorb" : "Ausverkauft"}
                </>
              )}
            </button>
          </div>
          <button
            onClick={buyNow}
            disabled={!product.available}
            className="w-full border border-[#c7bfae] text-[#17140f] text-[12px] tracking-[0.2em] uppercase py-4 mt-3 hover:border-gold hover:text-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Sofort kaufen
          </button>
          <p className="text-[#8d8779] text-xs text-center mt-3">
            Sichere Bezahlung & Versand über roidesrois.de
          </p>

          {/* Perks */}
          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-[#e7e0d3]">
            {[
              { icon: Truck, t: "Gratis Versand" },
              { icon: RotateCcw, t: "30 Tage Rückgabe" },
              { icon: ShieldCheck, t: "100% Original" },
            ].map((p, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <p.icon size={20} className="text-gold" />
                <span className="text-[#6b665a] text-xs">{p.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Notes / Tabs */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pb-20">
        <div className="flex gap-8 border-b border-[#e7e0d3] mb-8">
          {[
            { k: "beschreibung", l: "Duftpyramide" },
            { k: "story", l: "Der Duft" },
            { k: "details", l: "Details" },
            { k: "versand", l: "Versand" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`pb-4 text-[12px] tracking-[0.2em] uppercase transition-colors relative ${
                tab === t.k ? "text-gold" : "text-[#8d8779] hover:text-[#4a463c]"
              }`}
            >
              {t.l}
              {tab === t.k && (
                <span className="absolute bottom-0 left-0 w-full h-px bg-gold" />
              )}
            </button>
          ))}
        </div>

        {tab === "beschreibung" && (
          <div className="max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <NoteBlock title="Kopfnote" notes={product.top} />
              <NoteBlock title="Herznote" notes={product.heart} />
              <NoteBlock title="Basisnote" notes={product.base} />
            </div>
            {product.ingredientsImage && (
              <img
                src={product.ingredientsImage}
                alt={`Inhaltsstoffe ${product.name}`}
                className="w-full mt-10 rounded-sm border border-[#e7e0d3]"
                loading="lazy"
              />
            )}
          </div>
        )}
        {tab === "story" && (
          <div
            className="max-w-2xl text-[#4a463c] leading-relaxed rdr-prose"
            dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
          />
        )}
        {tab === "details" && (
          <div className="max-w-2xl text-[#4a463c] space-y-3">
            <DetailRow k="Duftfamilie" v={product.family} />
            <DetailRow k="Konzentration" v={product.concentration} />
            <DetailRow k="Inhalt" v={product.size} />
            <DetailRow k="Geschlecht" v={product.category} />
            <DetailRow k="Herkunft" v="Hergestellt in Frankreich" />
          </div>
        )}
        {tab === "versand" && (
          <div className="max-w-2xl text-[#4a463c] leading-relaxed space-y-4">
            <p>
              Kostenloser Versand ab €99 Bestellwert. Lieferung innerhalb von 2–4
              Werktagen in Deutschland.
            </p>
            <p>
              Nicht zufrieden? Du kannst deine Bestellung innerhalb von 30 Tagen
              kostenlos zurücksenden.
            </p>
          </div>
        )}
      </div>

      {/* Related */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pb-24">
        <h2 className="font-serif text-3xl md:text-4xl text-[#17140f] text-center mb-12">
          Das könnte dir gefallen
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {related.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </div>
    </div>
  );
};

const NoteBlock = ({ title, notes }) => (
  <div className="border border-[#e7e0d3] p-6 bg-[#f7f1e8]">
    <p className="text-gold text-[11px] tracking-[0.25em] uppercase mb-4">
      {title}
    </p>
    <ul className="space-y-2">
      {notes.map((n) => (
        <li key={n} className="text-[#4a463c] text-sm">
          {n}
        </li>
      ))}
    </ul>
  </div>
);

const DetailRow = ({ k, v }) => (
  <div className="flex justify-between border-b border-[#e7e0d3] py-3">
    <span className="text-[#8d8779] text-sm tracking-wide">{k}</span>
    <span className="text-[#17140f] text-sm">{v}</span>
  </div>
);

export default ProductDetail;
